import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT, IMAGES } from "@/i18n/translations";
import Reveal from "@/components/Reveal";
import AzulejoStrip from "@/components/AzulejoStrip";
import Seo from "@/components/Seo";

const About = () => {
  const { t, lang } = useLanguage();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <div data-testid="about-page">
      <Seo
        title={lang === "es" ? "Sobre Marina — Marina Knows" : "About Marina — Marina Knows"}
        description={lang === "es"
          ? "Marina: 15 años de experiencia ayudando a extranjeros con la burocracia española en la Comunidad Valenciana. Español nativo, inglés C2."
          : "Marina: 15 years of experience helping foreigners with Spanish bureaucracy in the Comunidad Valenciana. Native Spanish, fluent English (C2)."}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4" data-testid="about-overline">{t.about.overline}</p>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-ink mb-8" data-testid="about-title">{t.about.title}</h1>
          <p className="font-serif italic text-2xl md:text-[1.7rem] leading-relaxed text-bronze mb-10" data-testid="about-lead">
            {t.about.lead}
          </p>
          <div className="space-y-5 text-lg leading-relaxed text-ink/80">
            <p>{t.about.body1}</p>
            <p>{t.about.body2}</p>
            <p>{t.about.body3}</p>
          </div>
        </div>

        <div className="lg:col-span-5" ref={imgRef}>
          <Reveal>
            <div className="relative pb-12 pr-4 md:pr-8">
              <div className="overflow-hidden rounded-t-[8rem] rounded-b-2xl border border-stone">
                <motion.img
                  src={IMAGES.marina}
                  alt={t.about.photoAlt}
                  className="w-full h-[420px] md:h-[540px] object-cover object-[50%_12%] scale-110"
                  style={{ y: imgY }}
                  data-testid="about-photo"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, rotate: 10, y: 24 }}
                whileInView={{ opacity: 1, rotate: 4, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.25, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="absolute bottom-0 -right-1 md:right-0 w-32 md:w-44 bg-white p-2 pb-5 rounded-xl shadow-xl shadow-ink/10"
              >
                <img
                  src={IMAGES.marina2}
                  alt={t.about.photoAlt}
                  className="w-full h-32 md:h-44 object-cover rounded-lg"
                  data-testid="about-photo-2"
                />
              </motion.div>
            </div>
          </Reveal>
        </div>
      </section>

      <AzulejoStrip />

      <section className="bg-cream border-y border-stone">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <div className="bg-white border border-stone rounded-2xl p-10 text-center h-full" data-testid="about-experience">
              <p className="font-serif text-7xl text-bronze leading-none">{t.about.expTitle}</p>
              <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-stone-fg">{t.about.expYears}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="md:col-span-4">
            <div className="bg-white border border-stone rounded-2xl p-10 h-full" data-testid="about-languages">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-6">{t.about.langTitle}</p>
              <ul className="space-y-4">
                {t.about.langs.map((l, i) => (
                  <li key={i} className="flex justify-between items-baseline gap-4 border-b border-stone/70 pb-3 last:border-0">
                    <span className="font-medium text-ink">{l.lang}</span>
                    <span className="text-sm text-stone-fg text-right">{l.level}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.16} className="md:col-span-4">
            <div className="bg-white border border-stone rounded-2xl p-10 h-full" data-testid="about-trust">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-6">{t.about.trustTitle}</p>
              <ul className="space-y-4">
                {t.about.trust.map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink/80">
                    <Check size={18} className="text-olive mt-0.5 shrink-0" aria-hidden />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
        <Reveal>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-cta"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
          >
            <MessageCircle size={18} aria-hidden />
            {t.about.cta}
          </a>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
